import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const NavItem = ({ to, children, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `px-3 py-2 text-sm font-medium rounded-md transition ${
        isActive ? 'text-amber-400 bg-zinc-900' : 'text-zinc-300 hover:text-amber-400'
      }`
    }
  >
    {children}
  </NavLink>
);

const DealsLayout = ({ children, user, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-40 backdrop-blur-lg bg-black/80 border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <span className="text-2xl font-extrabold tracking-tight text-amber-400">Deal</span>
            <span className="text-2xl font-extrabold tracking-tight text-white">Ping</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/" testid="nav-home">Hot Deals</NavItem>
            <NavItem to="/deals" testid="nav-all-deals">All Deals</NavItem>
            <NavItem to="/subscribe" testid="nav-subscribe">Get Alerts</NavItem>
            <NavItem to="/tracker" testid="nav-tracker">Price Tracker</NavItem>
            {user && <NavItem to="/referrals" testid="nav-referrals">Refer & Earn</NavItem>}
            {user?.is_admin && <NavItem to="/admin" testid="nav-admin">Admin</NavItem>}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <span className="text-xs text-zinc-400">{user.email}</span>
                <button
                  onClick={onLogout}
                  data-testid="logout-btn"
                  className="text-sm px-3 py-1.5 rounded-md border border-zinc-700 hover:border-amber-400 text-zinc-200"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => nav('/login')}
                  data-testid="header-login-btn"
                  className="text-sm px-3 py-1.5 rounded-md text-zinc-200 hover:text-amber-400"
                >
                  Login
                </button>
                <button
                  onClick={() => nav('/subscribe')}
                  data-testid="header-subscribe-btn"
                  className="text-sm px-4 py-1.5 rounded-md bg-amber-400 text-black font-semibold hover:bg-amber-300"
                >
                  Get Free Alerts
                </button>
              </>
            )}
          </div>
          <button
            className="md:hidden text-amber-400"
            onClick={() => setMobileOpen(v => !v)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            ☰
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-zinc-900 bg-black px-4 py-3 flex flex-col gap-1">
            <NavItem to="/" testid="mnav-home">Hot Deals</NavItem>
            <NavItem to="/deals" testid="mnav-all-deals">All Deals</NavItem>
            <NavItem to="/subscribe" testid="mnav-subscribe">Get Alerts</NavItem>
            <NavItem to="/tracker" testid="mnav-tracker">Price Tracker</NavItem>
            {user && <NavItem to="/referrals" testid="mnav-referrals">Refer & Earn</NavItem>}
            {user?.is_admin && <NavItem to="/admin" testid="mnav-admin">Admin</NavItem>}
            {!user && <NavItem to="/login" testid="mnav-login">Login</NavItem>}
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-24 border-t border-zinc-900 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="text-amber-400 font-extrabold text-lg">DealPing</div>
            <p className="text-zinc-400 mt-2">
              Automated hot-deals discovery. We surface the best prices across the web so you stop overpaying.
            </p>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Top Categories</div>
            <ul className="space-y-1 text-zinc-400">
              <li><Link to="/deals/category/electronics" className="hover:text-amber-400">Best electronics deals</Link></li>
              <li><Link to="/deals/category/groceries" className="hover:text-amber-400">Best grocery deals</Link></li>
              <li><Link to="/deals/category/household" className="hover:text-amber-400">Best household deals</Link></li>
              <li><Link to="/deals/category/gifts" className="hover:text-amber-400">Best gifts under $25</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Top Stores</div>
            <ul className="space-y-1 text-zinc-400">
              <li><Link to="/deals/store/amazon" className="hover:text-amber-400">Best Amazon deals today</Link></li>
              <li><Link to="/deals/store/walmart" className="hover:text-amber-400">Best Walmart deals today</Link></li>
              <li><Link to="/deals/store/target" className="hover:text-amber-400">Best Target deals today</Link></li>
              <li><Link to="/deals/store/bestbuy" className="hover:text-amber-400">Best Best Buy deals today</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">More</div>
            <ul className="space-y-1 text-zinc-400">
              <li><Link to="/subscribe" className="hover:text-amber-400">Email alerts</Link></li>
              <li><Link to="/tracker" className="hover:text-amber-400">Track a product</Link></li>
              <li><Link to="/login" className="hover:text-amber-400">Login / Signup</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-900 py-4 text-center text-xs text-zinc-500">
          © {new Date().getFullYear()} DealPing · Some links may be affiliate links at no extra cost to you.
        </div>
      </footer>
    </div>
  );
};

export default DealsLayout;
